import {
  SMART_MAX_DURATION_SECONDS_MAX,
  SMART_MAX_DURATION_SECONDS_MIN,
  SMART_SILENCE_TIMEOUT_SECONDS_MAX,
  SMART_SILENCE_TIMEOUT_SECONDS_MIN,
  SMART_TRANSFER_MODE,
  toApiDestination,
  type DraftDestination,
} from '@/features/tools/components/transfer-call/transferDraft'
import type {
  NormalizedTransferDestination,
  TransferAssistantSettings,
  TransferCallSettings,
  TransferCallSettingsOverride,
  TransferPlan,
} from '@/features/tools/types'

export function normalizedTransferSettingsError(
  settings: TransferCallSettings
): string | null {
  if (settings.version !== 1 || !Array.isArray(settings.destinations)) {
    return 'Transfer Call settings are invalid.'
  }
  if (settings.destinations.length > 50) {
    return 'Transfer Call supports at most 50 destinations.'
  }
  for (const destination of settings.destinations) {
    if (destination.type !== 'number' || !destination.number.trim()) {
      return 'Every Transfer destination requires a phone number.'
    }
    const smart = destination.transferPlan?.mode === SMART_TRANSFER_MODE
    if (smart !== (destination.smartAssistant !== undefined)) {
      return 'Smart Transfer destinations require complete assistant settings.'
    }
    if (destination.smartAssistant) {
      const assistant = destination.smartAssistant
      if (
        !assistant.instructions.trim() ||
        !Number.isInteger(assistant.maxDurationSeconds) ||
        assistant.maxDurationSeconds < SMART_MAX_DURATION_SECONDS_MIN ||
        assistant.maxDurationSeconds > SMART_MAX_DURATION_SECONDS_MAX ||
        !Number.isInteger(assistant.silenceTimeoutSeconds) ||
        assistant.silenceTimeoutSeconds < SMART_SILENCE_TIMEOUT_SECONDS_MIN ||
        assistant.silenceTimeoutSeconds > SMART_SILENCE_TIMEOUT_SECONDS_MAX ||
        (assistant.firstMessageMode === 'assistant-speaks-first' &&
          !assistant.firstMessage.trim())
      ) {
        return 'Smart Transfer destinations require complete assistant settings.'
      }
    }
  }
  return null
}

function transferPlan(
  plan: TransferPlan | undefined
): TransferPlan | undefined {
  if (!plan) return undefined
  return {
    ...(plan.mode !== undefined ? { mode: plan.mode } : {}),
    ...(plan.sipVerb !== undefined ? { sipVerb: plan.sipVerb } : {}),
    ...(plan.fallbackPlan !== undefined
      ? {
          fallbackPlan: {
            ...(plan.fallbackPlan.message !== undefined
              ? { message: plan.fallbackPlan.message }
              : {}),
            ...(plan.fallbackPlan.endCallEnabled !== undefined
              ? { endCallEnabled: plan.fallbackPlan.endCallEnabled }
              : {}),
          },
        }
      : {}),
    ...(plan.contextEngineeringPlan !== undefined
      ? {
          contextEngineeringPlan:
            plan.contextEngineeringPlan.type !== undefined
              ? { type: plan.contextEngineeringPlan.type }
              : {},
        }
      : {}),
    ...(plan.summaryPlan !== undefined
      ? {
          summaryPlan: {
            ...(plan.summaryPlan.enabled !== undefined
              ? { enabled: plan.summaryPlan.enabled }
              : {}),
            ...(plan.summaryPlan.messages !== undefined
              ? {
                  messages: plan.summaryPlan.messages.map((message) => ({
                    ...(message.role !== undefined
                      ? { role: message.role }
                      : {}),
                    ...(message.content !== undefined
                      ? { content: message.content }
                      : {}),
                  })),
                }
              : {}),
          },
        }
      : {}),
  }
}

function smartAssistant(
  assistant: TransferAssistantSettings | undefined
): TransferAssistantSettings | undefined {
  if (!assistant) return undefined
  return {
    instructions: assistant.instructions,
    firstMessage: assistant.firstMessage,
    firstMessageMode: assistant.firstMessageMode,
    maxDurationSeconds: assistant.maxDurationSeconds,
    silenceTimeoutSeconds: assistant.silenceTimeoutSeconds,
    fallbackMessage: assistant.fallbackMessage,
    fallbackEndCallEnabled: assistant.fallbackEndCallEnabled,
    contextEngineering: assistant.contextEngineering,
  }
}

export function normalizedTransferDestination(
  draft: DraftDestination
): NormalizedTransferDestination {
  const destination = toApiDestination(draft)
  if (!destination.number) {
    throw new Error('Transfer destination requires a phone number')
  }
  return {
    type: 'number',
    number: destination.number,
    ...(destination.extension !== undefined
      ? { extension: destination.extension }
      : {}),
    ...(destination.description !== undefined
      ? { description: destination.description }
      : {}),
    ...(destination.message !== undefined
      ? { message: destination.message }
      : {}),
    ...(destination.transferPlan !== undefined
      ? { transferPlan: transferPlan(destination.transferPlan) }
      : {}),
    ...(destination.smartAssistant !== undefined
      ? {
          smartAssistant: smartAssistant(
            destination.smartAssistant as TransferAssistantSettings
          ),
        }
      : {}),
  }
}

function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`
  if (value !== null && typeof value === 'object') {
    const record = value as Record<string, unknown>
    return `{${Object.keys(record)
      .toSorted()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
      .join(',')}}`
  }
  return JSON.stringify(value)
}

export function transferSettingsOverride(
  destinations: NormalizedTransferDestination[],
  organizationSettings: TransferCallSettings
): TransferCallSettingsOverride | null {
  return canonicalJson(destinations) ===
    canonicalJson(organizationSettings.destinations)
    ? null
    : { version: 1, destinations }
}
